import React from "react";

// Minimal markdown renderer for Nick's replies: headings, paragraphs, **bold**,
// *italic*, `code`, [links](url), bullet/numbered lists, task checkboxes,
// horizontal rules and simple pipe tables.
function renderInline(text, keyPrefix) {
  const parts = [];
  let remaining = text;
  let idx = 0;
  const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/;
  let m;
  while ((m = regex.exec(remaining))) {
    if (m.index > 0) parts.push(<span key={`${keyPrefix}-t${idx++}`}>{remaining.slice(0, m.index)}</span>);
    if (m[2] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-b${idx++}`} className="font-semibold text-white">{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      parts.push(<em key={`${keyPrefix}-i${idx++}`} className="italic">{m[3]}</em>);
    } else if (m[4] !== undefined) {
      parts.push(
        <code key={`${keyPrefix}-c${idx++}`} className="font-mono text-[0.85em] bg-black/40 border border-neutral-700 rounded px-1 py-0.5 text-[#73bf44]">
          {m[4]}
        </code>
      );
    } else if (m[5] !== undefined) {
      parts.push(
        <a key={`${keyPrefix}-a${idx++}`} href={m[6]} target="_blank" rel="noreferrer" className="text-[#73bf44] underline underline-offset-2 hover:text-white">
          {m[5]}
        </a>
      );
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  if (remaining) parts.push(<span key={`${keyPrefix}-t${idx++}`}>{remaining}</span>);
  return parts;
}

const HEADING_CLS = {
  1: "text-lg font-bold text-white mt-3 mb-1.5 font-heading",
  2: "text-base font-bold text-white mt-3 mb-1 font-heading",
  3: "text-sm font-semibold text-white mt-2.5 mb-1 font-heading uppercase tracking-wide",
};

function isTableSep(line) {
  return /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes("-");
}

function splitCells(line) {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

export function MarkdownLite({ text }) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null;

  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag
        key={`l${blocks.length}`}
        className={`my-2 space-y-1.5 ${list.checkbox ? "list-none pl-1" : list.ordered ? "list-decimal pl-5" : "list-disc pl-5"} marker:text-[#73bf44]`}
      >
        {list.items.map((it, i) => (
          <li key={i} className="text-neutral-200">
            {list.checkbox ? (
              <span className="flex items-start gap-2">
                <span className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border text-[10px] shrink-0 ${it.checked ? "bg-[#73bf44] border-[#73bf44] text-[#050505]" : "border-neutral-600 text-transparent"}`}>✓</span>
                <span>{renderInline(it.text, `li${blocks.length}-${i}`)}</span>
              </span>
            ) : (
              renderInline(it.text, `li${blocks.length}-${i}`)
            )}
          </li>
        ))}
      </Tag>
    );
    list = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();
    const trimmed = line.trim();

    // table: header row + separator row
    if (trimmed.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushList();
      const header = splitCells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        rows.push(splitCells(lines[i]));
        i++;
      }
      i--;
      blocks.push(
        <div key={`tbl${blocks.length}`} className="my-3 overflow-x-auto">
          <table className="w-full text-xs sm:text-sm border border-neutral-800 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-black/40">
                {header.map((h, hi) => (
                  <th key={hi} className="text-left px-2 sm:px-3 py-2 text-white font-semibold border-b border-neutral-800 whitespace-nowrap">{renderInline(h, `th${blocks.length}-${hi}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-b border-neutral-800/60 last:border-0">
                  {r.map((c, ci) => (
                    <td key={ci} className="px-2 sm:px-3 py-2 text-neutral-300 align-top">{renderInline(c, `td${blocks.length}-${ri}-${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // blockquote / callout: one or more consecutive '> ' lines
    if (/^\s*>\s?/.test(line)) {
      flushList();
      const quote = [line.replace(/^\s*>\s?/, "")];
      while (i + 1 < lines.length && /^\s*>\s?/.test(lines[i + 1])) {
        i++;
        quote.push(lines[i].replace(/^\s*>\s?/, ""));
      }
      blocks.push(
        <blockquote key={`bq${blocks.length}`} className="my-2.5 border-l-2 border-[#73bf44] bg-[#73bf44]/[0.06] pl-3 pr-2 py-2 rounded-r text-neutral-300">
          {quote.map((q, qi) => (
            <p key={qi} className="leading-relaxed">{renderInline(q, `bq${blocks.length}-${qi}`)}</p>
          ))}
        </blockquote>
      );
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    const hr = /^(-{3,}|\*{3,}|_{3,})$/.test(trimmed);
    const checkbox = line.match(/^\s*[-*]\s+\[([ xX])\]\s+(.*)$/);
    const bullet = !checkbox && line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);

    if (heading) {
      flushList();
      const lvl = Math.min(heading[1].length, 3);
      blocks.push(<div key={`h${i}`} className={HEADING_CLS[lvl]}>{renderInline(heading[2], `h${i}`)}</div>);
    } else if (hr) {
      flushList();
      blocks.push(<hr key={`hr${i}`} className="my-3 border-neutral-800" />);
    } else if (checkbox) {
      if (!list || !list.checkbox) flushList();
      list = list && list.checkbox ? list : { checkbox: true, ordered: false, items: [] };
      list.items.push({ text: checkbox[2], checked: checkbox[1].toLowerCase() === "x" });
    } else if (bullet) {
      if (!list || list.ordered || list.checkbox) flushList();
      list = list && !list.ordered && !list.checkbox ? list : { ordered: false, items: [] };
      list.items.push({ text: bullet[1] });
    } else if (numbered) {
      if (!list || !list.ordered) flushList();
      list = list && list.ordered ? list : { ordered: true, items: [] };
      list.items.push({ text: numbered[1] });
    } else if (trimmed === "") {
      flushList();
    } else {
      flushList();
      blocks.push(
        <p key={`p${i}`} className="my-1.5 leading-relaxed text-neutral-200">
          {renderInline(line, `p${i}`)}
        </p>
      );
    }
  }
  flushList();
  return <div className="text-[0.95rem]">{blocks}</div>;
}
